import cx from 'classnames'
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useRef,
} from 'react'
import { useParallaxSlide } from 'src/hooks/useParallax'

import * as SC from './styled'

export type ImageProps = React.ImgHTMLAttributes<HTMLImageElement>

export type PictureProps = {
  className?: string
  imageProps?: ImageProps
  mobileImageProps?: ImageProps // optional if multiple formats are needed
  parallaxRatio?: number
}

type PictureRef = ForwardRefExoticComponent<
  PictureProps & RefAttributes<HTMLDivElement>
>

type PictureContentProps = PictureProps & {
  imageProps: ImageProps
}

// Inner component — always renders, so refs are always attached to DOM elements.
// Keeping hooks here prevents motion's useScroll from throwing "ref not hydrated"
// when the outer component returns null before reaching the JSX with ref={...}.
const PictureContent = forwardRef<HTMLDivElement, PictureContentProps>(
  ({ className, imageProps, mobileImageProps, parallaxRatio = 0.15 }, ref) => {
    const refDesktop = useRef<HTMLImageElement>(null)
    const refMobile = useRef<HTMLImageElement>(null)

    const { transform: transformDesktop } = useParallaxSlide({
      ratioY: parallaxRatio,
      ref: refDesktop,
    })
    const { transform: transformMobile } = useParallaxSlide({
      ratioY: parallaxRatio,
      ref: refMobile,
    })

    return (
      <SC.Container
        ref={ref}
        className={cx('Picture', className)}
        {...(imageProps['aria-hidden'] && { 'aria-hidden': true })}
      >
        <SC.DesktopImage
          ref={refDesktop}
          style={{ y: transformDesktop.y, scale: 1 + parallaxRatio }}
          {...(imageProps as any)}
        />
        {mobileImageProps && (
          <SC.MobileImage
            ref={refMobile}
            style={
              transformMobile !== undefined
                ? { y: transformMobile.y, scale: 1 + parallaxRatio }
                : {}
            }
            {...(mobileImageProps as any)}
          />
        )}
      </SC.Container>
    )
  }
)

PictureContent.displayName = 'PictureContent'

const Picture: PictureRef = forwardRef<HTMLDivElement, PictureProps>(
  (props, ref) => {
    if (props.imageProps === undefined) return null
    return <PictureContent {...props} imageProps={props.imageProps} ref={ref} />
  }
)

Picture.displayName = 'Picture'

export default Picture
